import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { message } from "antd";
import { ServiceContext } from "./ServiceContext";
import { BASE_URL } from "../../Helper/Helper";

import AdminState from "./ContextStore/AdminState";
import EnquiryState from "./ContextStore/EnquiryState";
import GlobalTemplateState from "./ContextStore/GlobalTemplateState";
import RolesPermissionState from "./ContextStore/RolesPermissionState";
import PropertyState from "./ContextStore/PropertyState";
import LeadState from "./ContextStore/LeadState";
import DataState from "./ContextStore/DataState";
import TeamsState from "./ContextStore/TeamsState";
import TaskState from "./ContextStore/TaskState";
import ProjectState from "./ContextStore/ProjectState";
import OrganizationState from "./ContextStore/OrganizationState";
import AttendanceState from "./ContextStore/AttendanceState";
import LeadFuncState from "./CommonStoreState/LeadFuncState";

const ServiceProvider = ({ children }) => {
  const [messageApi, contextHolder] = message.useMessage();
  const showAlert = (type, msg) => {
    messageApi.open({
      type: type,
      content: msg,
    });
  };

  let token = localStorage.getItem("token");
  let decodeToken = null;
  if (token && token.split(".").length === 3) {
    decodeToken = jwtDecode(token);
  }

  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(false);

  const formatIndianAmount = (amount) => {
    amount = Number(amount); // ensures stringified numbers work too

    if (amount >= 10000000) {
      return `${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `${(amount / 100000).toFixed(2)} Lacs`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(2)} K`;
    } else {
      return amount.toString(); // return raw value if below 1K
    }
  };

  const FormattedDate = (originalDate) => {
    const formatted = dayjs(originalDate, "ddd, D MMM, YYYY, h:mm:ss A").format(
      "D-MMM-YYYY"
    );

    return <span>{formatted}</span>;
  };

  const [userData, setUserData] = useState([]);
  const [openDashBar, setOpenDashBar] = useState(true);
  let user = decodeToken && decodeToken?._doc;

  const [roleObj, setRoleObj] = useState({});
  const getUser = async (signal) => {
    try {
      setLoading(true);

      const response = await axios.get(`${BASE_URL}/api/v2/admin/get-profile`, {
        headers: {
          token: token,
        },
        signal, // abort controller se link kar diya
      });

      let datas = response.data.user.filter((Item) => Item._id === user?._id)[0]
        ?.userRoles;

      const mergedPermissions = {};

      datas?.forEach((role) => {
        role.rolePermissions?.forEach((permissionObj) => {
          Object.entries(permissionObj).forEach(([key, values]) => {
            if (!mergedPermissions[key]) {
              mergedPermissions[key] = new Set(values);
            } else {
              values.forEach((v) => mergedPermissions[key].add(v));
            }
          });
        });
      });

      const finalObject = {};
      Object.entries(mergedPermissions).forEach(([key, valueSet]) => {
        finalObject[key] = Array.from(valueSet);
      });

      setUserData(response.data.user);
      setRoleObj(finalObject);
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request canceled:", error.message);
      } else if (error.name === "CanceledError") {
        console.log("Request aborted by signal");
      } else {
        console.error("Error fetching user:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const [leadData, setLeadData] = useState([]);
  const [dashLeadReportsData, setDashLeadReportsData] = useState([]);

  const [leadState, setLeadState] = useState({});
  // status count data end

  const loadLeadData = async () => {
    const status = leadData?.totals?.status;
    const source = leadData?.totals?.source;

    // meeting/Finance
    const leadMeetFinance = await LeadFuncState.getformatUserPerformanceData(
      dashLeadReportsData?.groupedByUser || []
    );

    // LeadReport
    const leadReport = await LeadFuncState.getformatLeadReportData(
      dashLeadReportsData?.userwiseTotals || []
    );

    // Team Performance
    const leadTeamPerfom = await LeadFuncState.getformatTeamPerformanceData(
      dashLeadReportsData?.userwiseTotals || []
    );

    const leadDatas = {
      status,
      source,
      leadReport,
      leadTeamPerfom,
      leadMeetFinance,
    };
    setLeadState(leadDatas);
  };

  useEffect(() => {
    loadLeadData();

    // eslint-disable-next-line
  }, [leadData, dashLeadReportsData]);

  // ~~~~~~$$$$$$$$ State and Function for Lead Table Data Fetching $$$$$$$$~~~~~~
  const getData = async () => {
    try {
      setLoading(true);
      const res = await LeadState.getDashLead();
      const dashLeadReports = await LeadState.getDashLeadReports();
      setDashLeadReportsData(dashLeadReports);

      setLeadData(res);
    } catch (error) {
      console.error("Fetching data failed:", error);
      setLeadData([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
    setLoading(false);
    //eslint-disable-next-line
  }, [loading]);

  // 🔄 Hook me use karo
  useEffect(() => {
    const controller = new AbortController();

    if (token) {
      getUser(controller.signal);
    }

    return () => {
      controller.abort(); // unmount hote hi request cancel
    };

    //eslint-disable-next-line
  }, []);

  return (
    <ServiceContext.Provider
      value={{
        AdminState: AdminState,
        PropertyState: PropertyState,
        EnquiryState: EnquiryState,
        GlobalTemplateState: GlobalTemplateState,
        RolesPermissionState: RolesPermissionState,
        LeadState: LeadState,
        DataState: DataState,
        TeamsState: TeamsState,
        TaskState: TaskState,
        ProjectState: ProjectState,
        OrganizationState: OrganizationState,
        AttendanceState: AttendanceState,

        // misc
        showAlert,
        contextHolder,
        decodeToken,
        refresh,
        setRefresh,
        loading,
        setLoading,
        formatIndianAmount,
        openDashBar,
        setOpenDashBar,
        FormattedDate,
        roleObj,
        userData,
        leadData,
        leadState,
      }}
    >
      {children}
    </ServiceContext.Provider>
  );
};

export default ServiceProvider;
